import { ExternalLink, MessageSquare, Rocket, Video } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useMarketingResources } from './hooks/useMarketingResources';

export const MarketingResourcesCard = () => {
  const { data, isLoading, isError } = useMarketingResources();

  if (isLoading) {
    return <Skeleton data-testid="marketing-resources-skeleton" className="h-40 w-full" />;
  }

  if (isError || !data) return null;

  const { communicationAssets, webinarLink, advisorAcceleratorLink } = data;
  const hasComms = communicationAssets?.enabled && communicationAssets?.url;
  const hasWebinar = webinarLink?.enabled && webinarLink?.url;
  const hasAccelerator = advisorAcceleratorLink?.enabled && advisorAcceleratorLink?.url;

  if (!hasComms && !hasWebinar && !hasAccelerator) return null;

  return (
    <div className="mt-6">
      <h2 className="mb-1 text-lg font-semibold text-gray-800">Marketing Resources</h2>
      <p className="mb-4 text-sm text-gray-600">
        Resources provided by bQuest to help grow your practice.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {hasComms && communicationAssets && (
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">{communicationAssets.title || 'Communication Assets'}</h3>
            </div>
            {communicationAssets.description && (
              <p className="text-sm text-gray-600">{communicationAssets.description}</p>
            )}
            <Button asChild size="sm" variant="outline" className="mt-auto">
              <a href={communicationAssets.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1 h-3 w-3" />
                View Assets
              </a>
            </Button>
          </Card>
        )}

        {hasWebinar && webinarLink && (
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Webinar Recordings</h3>
            </div>
            {webinarLink.description && (
              <p className="text-sm text-gray-600">{webinarLink.description}</p>
            )}
            <Button asChild size="sm" variant="outline" className="mt-auto">
              <a href={webinarLink.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1 h-3 w-3" />
                {webinarLink.buttonLabel || 'View Recordings'}
              </a>
            </Button>
          </Card>
        )}

        {hasAccelerator && advisorAcceleratorLink && (
          <Card className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium">Advisor Accelerator</h3>
            </div>
            {advisorAcceleratorLink.description && (
              <p className="text-sm text-gray-600">{advisorAcceleratorLink.description}</p>
            )}
            <Button asChild size="sm" variant="outline" className="mt-auto">
              <a href={advisorAcceleratorLink.url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1 h-3 w-3" />
                {advisorAcceleratorLink.buttonLabel || 'View Advisor Accelerator'}
              </a>
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};
